import React, { memo } from 'react';

import { useSelector } from 'react-redux';

import PropTypes from 'prop-types';

import clsx from 'clsx';
import _get from 'lodash/get';

import { useFeatures } from '@atc/features';
import { ClientOnly } from '@atc/react-client-only';
import { useDevice } from '@atc/react-device';

import { EmailCTA } from 'reaxl-email';
import { OwnerChat, OwnerPhoneNumber, OwnerTextCTA } from 'reaxl-molecules';

import vdpResultsDuck from '@/ducks/vdp/vdpResultsDuck.js';

import useDealerTextMessage from '@/hooks/useDealerTextMessage';

/**
 * OwnerContact component displays dealer contact CTAs with icons and responsive layout
 *
 * Renders Phone, Email, Text, and Chat CTAs based on:
 * - Feature flag settings
 * - Device type (mobile vs desktop)
 * - Dealer configuration and capabilities
 * - Available contact methods
 *
 * The component uses conditional rendering to show only available contact methods
 * and automatically adjusts layout based on the number of CTAs displayed.
 *
 * @param {Object} [props.analyticsData] - Analytics data object passed to CTA components for tracking
 * @param {Function} [props.onEmailCTAClick] - Callback function triggered when email CTA is clicked
 * @param {Function} [props.chatClickHandler] - Callback function triggered when chat CTA is clicked
 * @param {Function} [props.textClickHandler] - Callback function triggered when text CTA is clicked
 * @param {Function} [props.phoneClickHandler] - Callback function triggered when phone CTA is clicked
 * @returns {React.ReactElement|null} The rendered contact CTAs or null if no CTAs are available
 */
const OwnerContact = memo(({
    analyticsData,
    chatClickHandler,
    disableContactCTAs = false,
    onEmailCTAClick,
    phoneClickHandler,
    textClickHandler,
}) => {

    const device = useDevice();
    const isXs = _get(device, 'is.xs', false);
    const textBody = useDealerTextMessage();

    const {
        call: [isCallEnabled],
        chat: [
            isChatEnabled,
            {
                providers: chatProviders,
                enable_mobile_chat: isMobileChatEnabled,
            },
        ],
        text: [isTextEnabled],
    } = useFeatures(['call', 'chat', 'text']);

    const listing = useSelector(vdpResultsDuck.selectors.getVdpActiveInventory) || {};
    const { id: inventoryId, owner = {}, clickType } = listing;
    const { chat = {}, phone, textNumber, hideEmailForm } = owner;

    const showPhoneCta = !!(isCallEnabled && phone?.value && isXs);
    const showEmailCta = !hideEmailForm;
    const showTextCta = !!(isTextEnabled && textNumber && isXs);
    const showChatCta = !!(isChatEnabled && (!isXs || isMobileChatEnabled) && !!chat?.provider && chatProviders?.includes(chat?.provider));
    const totalCtas = [showPhoneCta, showEmailCta, showTextCta, showChatCta].filter(Boolean).length;

    if (!totalCtas || disableContactCTAs) return null;
    return (
        <div className={clsx('display-flex flex-wrap', { 'justify-content-between': totalCtas >= 3, 'justify-content-left gap-4 gap-sm-5': totalCtas < 3 })}>
            {showPhoneCta && (
                <OwnerPhoneNumber
                    analyticsData={analyticsData}
                    phoneClickHandler={phoneClickHandler}
                    phone={phone}
                    uiContext="iconbutton-horizontal"
                    buttonStyle="link"
                    buttonLabel="Phone"
                />
            )}
            {showEmailCta && (
                <EmailCTA
                    type="iconbutton-horizontal"
                    listingId={inventoryId}
                    onClick={onEmailCTAClick}
                    clickType={clickType}
                    context="ownerDetails"
                    labelKey="email"
                />
            )}
            {showTextCta && (
                <OwnerTextCTA
                    analyticsData={{
                        ...analyticsData,
                        placement: 'dealer-card',
                    }}
                    textClickHandler={textClickHandler}
                    label="Text"
                    phoneNumber={textNumber}
                    textBody={textBody}
                    uiContext="iconbutton-horizontal"
                />
            )}
            {showChatCta && (
                <ClientOnly>
                    <OwnerChat
                        {...chat}
                        analyticsData={analyticsData}
                        chatClickHandler={chatClickHandler}
                        listing={listing}
                        merchantId={owner.id}
                        uiContext="iconButton"
                        label="Chat"
                    />
                </ClientOnly>
            )}
        </div>

    );
});

OwnerContact.propTypes = {
    /**
     * Analytics data object containing tracking information passed to CTA components.
     * Used for measuring user interactions and conversion rates across different contact methods.
     */
    analyticsData: PropTypes.object,
    /**
     * Boolean to disable phone/chat/email for the Hyundai experience
     */
    disableContactCTAs: PropTypes.bool,
    /**
     * The callback function fires when email CTA is clicked
     */
    onEmailCTAClick: PropTypes.func,
    /**
     * The callback function fires when chat CTA is clicked
     */
    chatClickHandler: PropTypes.func,
    /**
     * The callback function fires when owner phone is called
     */
    phoneClickHandler: PropTypes.func,
    /**
     * The callback function fires when text CTA is clicked
     */
    textClickHandler: PropTypes.func,
    /**
     * The uiContext for both phone and text CTAs
     */
    contactButtonUiContext: PropTypes.oneOf([
        'button',
        'iconbutton',
        'iconbutton-horizontal',
    ]),
};

export default OwnerContact;
